<script setup lang="ts">
import {
  disabled,
  image,
  download,
  inbox_in,
} from "pn-design-assets/pn-assets/icons";
import type { ImageCategory } from "@/rvapi";
import type { Frame } from "@/types/frames";
import { ref, onMounted, computed, watchEffect } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useImageStore } from "@/stores/images";
import { useFrames } from "@/stores/frames";
import type { PnFileUpload } from "@postnord/web-components/components/pn-file-upload";
import type { PnUploadFileItem } from "@postnord/web-components/types/globals/types";

const router = useRouter();
const route = useRoute();
const imageStore = useImageStore();
const framesStore = useFrames();
const uploadError = ref("");
const base64 = ref("");
const filename = ref("");
const frameId = route.params.id as string;
const frame = ref<Frame>({} as Frame);
const categories = ref<Record<number, ImageCategory>>({});
const selectedCategory = ref<ImageCategory | undefined>();
const saving = ref(false);
const saveReminder = ref(false);
const swedishAltText = ref("");
const englishAltText = ref("");
const isError = ref(false);
const errorDescription = ref("");

const imageUrl = computed(() => {
  if (base64.value) {
    return `data:image/png;base64,${base64.value}`;
  }
  return frame?.value?.thumbUrl;
});

const uploadWidget = ref(null as PnFileUpload | null);

const filesAdded = (e: any) => {
  if (e.detail.length > 0) {
    const errorFiles = e.detail.filter((d: any) => d.error);

    if (errorFiles.length > 0) {
      errorFiles.forEach((d: any) => {
        e.target.removeFile(d);
      });
    } else {
      uploadError.value = "";
      e.target.startUpload();
    }
    saveReminder.value = true;
  }
};

const uploadFile = async (e: any) => {
  const file: PnUploadFileItem = e.detail.file;
  base64.value = file.base64;
  filename.value = file.filename;
  await uploadWidget.value?.removeFile(file);
};

const fileuploaderror = (e: any) => {
  uploadError.value = e.detail.message.toString();
};

const languageChange = (e: any) => {
  const lang = e.target.id;
  const prevState = frame.value.languages.includes(lang);
  if (prevState !== e.target.checked) {
    if (e.target.checked) {
      frame.value.languages.push(lang);
    } else {
      frame.value.languages = frame.value.languages.filter((l) => l !== lang);
    }
  }
};

const canSave = computed(() => {
  return (
    frame.value.languages &&
    frame.value.languages.length > 0 &&
    imageUrl &&
    swedishAltText.value !== "" &&
    englishAltText.value !== ""
  );
});

const save = async () => {
  try {
    resetError();
    if (!frame.value) {
      return;
    }
    saving.value = true;
    const uploadObj = {} as any;

    uploadObj.hidden = frame.value.hidden;
    uploadObj.languages = frame.value.languages;
    uploadObj.groupId = frame.value.groupId;

    if (base64.value) {
      uploadObj.base64 = base64.value;
      uploadObj.filename = filename.value;
    }
    if (selectedCategory.value) {
      uploadObj.categories = [selectedCategory.value.categoryId];
    }
    uploadObj.imageDescription = [
      {
        language: "swedish",
        description: swedishAltText.value,
      },
      {
        language: "english",
        description: englishAltText.value,
      },
    ];

    await framesStore.updateFrame(frameId, uploadObj);
    router.push("/frames");
  } catch (e) {
    isError.value = true;
    errorDescription.value = "There was an error in saving the changes.";
    window.scrollTo(0, 0);
    console.log(e);
  } finally {
    saving.value = false;
  }
};
const loading = ref(true);
const refreshCategories = async () => {
  const result = await imageStore.getImageCategories();
  for (const c of result) {
    categories.value[c.categoryId] = c;
  }
};
const setSelectedCategory = (frame: Frame) => {
  if (frame.categories && frame.categories.length) {
    const cat = frame.categories[0];
    if (cat) selectedCategory.value = categories.value[cat];
  }
};
onMounted(async () => {
  resetError();
  loading.value = true;
  await refreshCategories();
  if (frameId !== "new") {
    frame.value = await framesStore.getFrame(frameId);
    setSelectedCategory(frame.value);
    swedishAltText.value = getAltText("swedish");
    englishAltText.value = getAltText("english");
  } else {
    frame.value = {
      languages: ["swedish"],
      frameId: 0,
      hidden: false,
      sortOrder: 1,
      imageUrl: "",
      aspect: "",
      thumbUrl: "",
      previewUrl: "",
      categories: [],
      imageDescription: [],
      groupId: "",
    };
  }
  loading.value = false;
});

watchEffect(() => {
  if (uploadWidget.value) {
    uploadWidget.value.addEventListener("filesAdded", filesAdded);
    uploadWidget.value.addEventListener("uploadFile", uploadFile);
    uploadWidget.value.addEventListener("fileuploaderror", fileuploaderror);
  }
});

const getAltText = (language: string) => {
  let description = "";
  if (frame?.value?.imageDescription?.length > 0) {
    const imageDescriptionArr = frame.value.imageDescription;
    const descriptionObj = imageDescriptionArr.find((imageDescObj) => {
      return imageDescObj.language === language;
    });
    if (descriptionObj) {
      description = descriptionObj.description;
    }
  }
  return description;
};

const resetError = () => {
  isError.value = false;
  errorDescription.value = "";
};
</script>

<template>
  <pn-toast
    closable="false"
    appearance="error"
    v-show="isError"
    :text="errorDescription"
  ></pn-toast>
  <h2>Edit frame #{{ frameId }}</h2>
  <div v-if="frame.groupId">
    <h3>Group Id:</h3>
    <span> {{ frame.groupId }} </span>
  </div>
  <h3>Visible for language:</h3>
  <pn-spinner size="4" v-show="loading" />
  <p>
    <label for="swedish">
      <pn-checkbox
        checkboxid="swedish"
        :checked="frame?.languages && frame.languages.includes('swedish')"
        @change="languageChange"
      />
      Swedish
    </label>
    <label for="english">
      <pn-checkbox
        checkboxid="english"
        :checked="frame?.languages && frame.languages.includes('english')"
        @change="languageChange"
      />
      English
    </label>
    <label for="danish">
      <pn-checkbox
        checkboxid="danish"
        :checked="frame?.languages && frame.languages.includes('danish')"
        @change="languageChange"
      />
      Danish
    </label>
    <label for="norwegian">
      <pn-checkbox
        checkboxid="norwegian"
        :checked="frame?.languages && frame.languages.includes('norwegian')"
        @change="languageChange"
      />
      Norwegian
    </label>
  </p>
  <p>
    <label for="hidden">
      <pn-checkbox
        checkboxid="hidden"
        :checked="frame.hidden"
        @change="frame.hidden = $event.target.checked"
      />
      Completely hide frame
    </label>
  </p>
  <p>Category: (used to hide seasonal frames)</p>
  <div v-if="!loading">
    <select v-model="selectedCategory">
      <option disabled>Select category</option>
      <option v-for="c in categories" :key="c.categoryId" :value="c">
        {{ c.swedish || c.english || c.danish || c.norwegian }}
      </option>
    </select>
  </div>

  <h3>Frame image</h3>

  <div class="grid-two-columns">
    <div class="icon">
      <img v-show="imageUrl" :src="imageUrl" />
      <div v-show="!imageUrl">
        <pn-icon :icon="disabled" class="middle" color="gray400"></pn-icon>
      </div>
    </div>

    <div>
      <pn-file-upload
        ref="uploadWidget"
        accept=".png"
        max-size="1mb"
        disabled="false"
        multiple="no"
        show-spinner="true"
      >
        <div class="message">
          <p class="title">
            <pn-icon :icon="image"></pn-icon>
            <pn-icon :icon="download"></pn-icon>
            <pn-icon :icon="inbox_in"></pn-icon>
          </p>
          <p class="description">
            2539x1807px, png, transparent background<br />
            To {{ frame.imageUrl ? "Replace" : "Upload" }}, drag image here or
            <a href="#" class="pn-open-file-explorer">browse</a>
          </p>
        </div>
      </pn-file-upload>
      <pn-toast
        appearance="error"
        v-show="uploadError"
        :text="uploadError"
      ></pn-toast>
      <pn-toast
        v-show="saveReminder"
        text="Don't forget to save your changes."
      ></pn-toast>
    </div>
  </div>

  <h3>Alternative Text</h3>
  <div>
    <p>
      <pn-input
        placeholder="Swedish Alternative Text (required *)"
        v-model="swedishAltText"
      ></pn-input>
    </p>
    <p>
      <pn-input
        placeholder="English Alternative Text (required *)"
        v-model="englishAltText"
      ></pn-input>
    </p>
  </div>
  <div>
    <hr />
    <span v-show="canSave" style="display: block; margin-top: 2rem">
      <pn-button :loading="saving" @click="save">Save changes</pn-button>
    </span>
  </div>
</template>

<style lang="scss" scoped>
pn-file-upload {
  .pn-file-upload-container {
    width: 400px;
  }
  pn-icon svg {
    width: 6rem;
    height: 6rem;
  }
}

.icon {
  min-width: 200px;
  min-height: 200px;
  padding: 1rem;

  img {
    border: 1px dashed gray;
    border-radius: 1rem;
    width: 200px;
  }
}
</style>
